<template>
    <div>
        <div
            id="hotkeysGuide"
            class="hotkeys"
            tabindex="0"
        >
            <fa :icon="['fas', 'lightbulb']" size="sm"/>
        </div>
        <b-tooltip target="hotkeysGuide" triggers="click blur" placement="topleft" custom-class="hotkeys-tooltip">
            <div class="hk">
                <div class="hk__header">
                    <div class="hk__header-badge">
                        <fa :icon="['fas', 'keyboard']"/>
                    </div>
                    <div class="hk__header-text">
                        <span class="hk__header-title">Hotkeys Guide</span>
                        <span class="hk__header-sub">Keyboard &amp; mouse shortcuts</span>
                    </div>
                </div>

                <div class="hk__body">
                    <div class="hk__group" v-for="(group, gIndex) in groups" :key="gIndex">
                        <div class="hk__group-label">
                            <fa :icon="['fas', group.icon]" class="hk__group-icon"/>
                            {{ group.label }}
                        </div>
                        <div class="hk__row" v-for="(item, index) in group.items" :key="index">
                            <div class="hk__keys">
                                <template v-for="(key, kIndex) in item.keys">
                                    <span
                                        v-if="key.sep"
                                        :key="'sep-' + kIndex"
                                        class="hk__sep"
                                    >{{ key.sep }}</span>
                                    <kbd
                                        v-else
                                        :key="'key-' + kIndex"
                                        class="hk__key"
                                        :class="{ 'hk__key--wide': key.wide }"
                                    >{{ key.label }}</kbd>
                                </template>
                            </div>
                            <div class="hk__desc">{{ item.desc }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </b-tooltip>
    </div>
</template>

<script>
export default {
    name: 'MMP_HotkeysInfo',
    data() {
        return {
            groups: [
                {
                    label: 'Navigation',
                    icon: 'arrows-alt',
                    items: [
                        { keys: [{ label: 'Double Click', wide: true }], desc: 'Zoom in one level' },
                        { keys: [{ label: 'Shift' }, { sep: '+' }, { label: 'Drag', wide: true }], desc: 'Zoom into the drawn rectangle' }
                    ]
                },
                {
                    label: 'Menus',
                    icon: 'bars',
                    items: [
                        { keys: [{ label: 'E' }], desc: 'Open / Close Extents menu' },
                        { keys: [{ label: 'B' }], desc: 'Open / Close Base Maps menu' },
                        { keys: [{ label: 'C' }], desc: 'Open / Close Coordinates menu' },
                        { keys: [{ label: '◀' }, { sep: '/' }, { label: '▶' }], desc: 'Iterate extents & base map options (when open)' }
                    ]
                },
                {
                    label: 'Layers & Lidar',
                    icon: 'layer-group',
                    items: [
                        { keys: [{ label: 'L' }], desc: 'Toggle 2D Lidar on / off (zoom 15+)' },
                        { keys: [{ label: '▲' }, { sep: '/' }, { label: '▼' }], desc: 'Iterate Lidar or ANSI / DRT layers' }
                    ]
                },
                {
                    label: 'General',
                    icon: 'info-circle',
                    items: [
                        { keys: [{ label: 'Enter' }], desc: 'Toggle layer / Lidar, or switch base map / extent' },
                        { keys: [{ label: 'Esc' }], desc: 'Return to home screen' }
                    ]
                }
            ]
        }
    }
}
</script>

<style>
/* ── Tooltip shell (overrides Bootstrap defaults) ── */
.hotkeys-tooltip {
  max-width: 460px !important;
  opacity: 1 !important;
}
.hotkeys-tooltip .tooltip-inner {
  max-width: 460px !important;
  width: 460px;
  padding: 0 !important;
  background: transparent !important;
  color: #063c6d !important;
  text-align: left !important;
  box-shadow: none !important;
  border: none !important;
  transform: none !important;
}
.hotkeys-tooltip .arrow {
  display: none !important;
}

/* ── Card ── */
.hk {
  text-align: left;
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(6, 60, 109, 0.3);
  border: 1px solid rgba(6, 60, 109, 0.1);
}

/* ── Header ── */
.hk__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #063c6d 0%, #0a5ca3 100%);
  color: #fff;
}
.hk__header-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.14);
  color: #ffd257;
  font-size: 15px;
  flex-shrink: 0;
}
.hk__header-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.hk__header-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.hk__header-sub {
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.7);
}

/* ── Body / groups ── */
.hk__body {
  padding: 6px 0 10px;
}
.hk__group {
  padding: 2px 0;
}
.hk__group + .hk__group {
  margin-top: 2px;
  border-top: 1px solid rgba(6, 60, 109, 0.07);
}
.hk__group-label {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px 4px;
  font-size: 9.5px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(6, 60, 109, 0.5);
}
.hk__group-icon {
  width: 13px;
  height: 13px;
  font-size: 11px;
  line-height: 1;
  color: #0a5ca3;
}

/* ── Rows ── */
.hk__row {
  display: grid;
  grid-template-columns: 132px 1fr;
  align-items: center;
  gap: 12px;
  padding: 5px 18px;
  border-radius: 7px;
  transition: background 0.12s ease;
}
.hk__row:hover {
  background: rgba(6, 60, 109, 0.05);
}

/* ── Keys column ── */
.hk__keys {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.hk__key {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  background: linear-gradient(180deg, #ffffff 0%, #e9eef5 100%);
  color: #063c6d;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  border: 1px solid rgba(6, 60, 109, 0.25);
  border-bottom-width: 2px;
  border-radius: 6px;
  box-shadow: 0 1px 1.5px rgba(6, 60, 109, 0.15);
}
.hk__key--wide {
  font-size: 10px;
  letter-spacing: 0.02em;
}
.hk__sep {
  color: rgba(6, 60, 109, 0.45);
  font-size: 11px;
  font-weight: 600;
}

/* ── Description column ── */
.hk__desc {
  color: #1f3b57;
  font-size: 12.5px;
  line-height: 1.4;
}
</style>

<style scoped>
.hotkeys {
  width: 22px;
  height: 22px;
  background-color: #ffffff;
  color: #063c6d;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  outline: 1px solid rgba(6, 60, 109, 0.25);
  cursor: pointer;
  transition: color 0.15s ease, outline-color 0.15s ease, box-shadow 0.15s ease;
}
.hotkeys:hover {
  color: #ffbe0b;
  outline: 1px solid #063c6d;
  box-shadow: 0 0 8px rgba(6, 60, 109, 0.25);
}
</style>
